/* eslint-disable no-use-before-define, react-hooks/exhaustive-deps */
import type { FC, ReactElement } from "react"
import { Fragment, useMemo } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { AnimatePresence, motion } from "framer-motion"
import find from "lodash-es/find"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import { type Params, useParams, useSearchParams } from "react-router-dom"

import {
    ESpinnerSize,
    ESpinnerVariant,
    ECoachNavigationStepperMode as EStepperMode,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Spinner,
    CoachNavigationStepper as Stepper,
    type TCoachNavigationStepperBullet as TStepperBullet,
    Typography
} from "@/3514/components"
import { sessionTimeStateUtils } from "@/3514/hooks"
import { type TParticipantByIdQuery, useParticipantByIdQuery } from "@/3514/queries"
import { type IEnrollmentModel, type IUserParticipantModel } from "@/3514/types"
import {
    type IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"

import {
    type TCoachParticipantModuleByIdQuery as TSuspenseQuery,
    useCoachParticipantModuleByIdQuery as useSuspenseQuery
} from "./coach.group-participant.api.queries.hooks"
import elements from "./coach.group-participant.elements"
import classes from "./coach.group-participant.styles"
import type { TCoachGroupParticipantSharedTabProps as TProps } from "./coach.group-participant.ui.types"
import uiUtils from "./coach.group-participant.ui.utils"

const CoachGroupParticipantTabModules: FC<TProps> = ({ participantId }: TProps): ReactElement => {
    const qc: QueryClient = useQueryClient()

    const {
        data: participant,
        dataUpdatedAt: participantUpdatedAt,
        isFetching: isParticipantFetching,
        isSuccess: isParticipantSuccess,
        isError: isParticipantError
    }: TParticipantByIdQuery = useParticipantByIdQuery({
        userId: participantId
    })

    const { t }: IUseTranslation = useTranslation()

    const getValueFromTranslatedObjectFunction: TGetValueFromTranslatedObjectFunction =
        useGetValueFromTranslatedObject()

    const { groupId: groupIdParam }: Readonly<Params> = useParams()

    const [searchParams]: ReturnType<typeof useSearchParams> = useSearchParams()

    const moduleIdParam: string = searchParams.get("moduleId")

    const bullets: (TStepperBullet & { isStub?: boolean })[] = useMemo<(TStepperBullet & { isStub?: boolean })[]>(
        (): (TStepperBullet & { isStub?: boolean })[] =>
            uiUtils.getModulesStepperBullets({
                journeyLength: isParticipantFetching || isParticipantError ? 3 : participant.journeyLength,
                enrollments: isParticipantFetching || isParticipantError ? [] : participant.enrollments,
                isQuerySuccess: isParticipantSuccess && !isParticipantError,
                moduleId: Number(moduleIdParam),
                groupId: Number(groupIdParam),
                participantId,
                qc,
                translator: getValueFromTranslatedObjectFunction,
                t
            }),
        [
            isParticipantFetching,
            isParticipantError,
            isParticipantSuccess,
            participant?.enrollments,
            moduleIdParam,
            groupIdParam,
            participantId
        ]
    )

    const currentBullet: TStepperBullet & { isStub?: boolean } = find<TStepperBullet>(bullets, {
        pageId: Number(moduleIdParam)
    })

    const currentEnrollment: IEnrollmentModel = find<IEnrollmentModel>(participant?.enrollments, {
        ...(Number(moduleIdParam) < 0 && { rank: Math.abs(Number(moduleIdParam)) }),
        ...(Number(moduleIdParam) > 0 && { id: Number(moduleIdParam) })
    })

    return (
        <section className={classes.modulesTab.container()}>
            <div className={classes.modulesTab.heading({ isLoaded: isParticipantSuccess })}>
                <Typography
                    text="Modules"
                    size={ETypographySize.Heading2}
                    tag={ETypographyTag.Heading2}
                    weight={ETypographyWeight.Medium}
                    family={ETypographyFamily.DmSerif}
                    color={ETypographyColor.Black}
                />

                <Stepper
                    key={participantUpdatedAt}
                    bullets={bullets}
                    mode={EStepperMode.Modules}
                    wrapperClassName={classes.modulesTab.stepper({ isLoading: isParticipantFetching })}
                />
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    key={`cgptm-${moduleIdParam}`}
                    style={{ display: "flex", flexDirection: "column", gap: "20px" }}
                >
                    <elements.modulesTab.EmptyBullets isVisible={isEmpty(bullets)} />

                    {!isEmpty(moduleIdParam) ? (
                        ((): ReactElement => {
                            const _isNegativeParam: boolean = Number(moduleIdParam) < 0
                            const _isModuleQueued: boolean = currentEnrollment?.status === "queued"
                            const _isModuleComplete: boolean = currentEnrollment?.status === "complete"
                            const _isModuleProgress: boolean = currentEnrollment?.status === "in_progress"
                            const _isSessionUpcoming: boolean = sessionTimeStateUtils.getIsSessionUpcoming({
                                startDate: currentEnrollment?.sessionTime,
                                durationInMins: currentEnrollment?.sessionDuration
                            })
                            const _isSessionPast: boolean = sessionTimeStateUtils.getIsSessionPast({
                                startDate: currentEnrollment?.sessionTime,
                                durationInMins: currentEnrollment?.sessionDuration
                            })

                            return Boolean(
                                Boolean(currentBullet?.isStub) ||
                                    _isNegativeParam ||
                                    _isModuleQueued ||
                                    Boolean(
                                        (_isSessionUpcoming || _isSessionPast) &&
                                            !_isModuleComplete &&
                                            !_isModuleProgress
                                    )
                            ) ? (
                                <Fragment>
                                    <elements.modulesTab.QueuedModule
                                        isVisible={_isModuleQueued}
                                        date={currentEnrollment?.firstPhaseStartDate}
                                        name={getValueFromTranslatedObjectFunction(currentEnrollment?.name)}
                                    />

                                    <elements.modulesTab.UnassignedModuleFuture
                                        isVisible={
                                            _isNegativeParam && (_isSessionUpcoming || Boolean(currentBullet?.isStub))
                                        }
                                    />

                                    <elements.modulesTab.UnassignedModulePast
                                        isVisible={_isNegativeParam && _isSessionPast}
                                    />
                                </Fragment>
                            ) : (
                                <Fragment>
                                    {isParticipantSuccess && (
                                        <Container
                                            participantId={participantId}
                                            moduleId={Number(moduleIdParam)}
                                            participant={participant}
                                        />
                                    )}

                                    {isParticipantSuccess && (
                                        <elements.modulesTab.Navigation
                                            bullets={bullets}
                                            currentBullet={currentBullet}
                                            isFetching={isParticipantFetching}
                                        />
                                    )}
                                </Fragment>
                            )
                        })()
                    ) : (
                        <elements.modulesTab.EmptyParam isVisible />
                    )}
                </motion.div>
            </AnimatePresence>
        </section>
    )
}

CoachGroupParticipantTabModules.displayName = "CoachGroupParticipantTabModules"

export default CoachGroupParticipantTabModules

function Container({
    participantId,
    moduleId,
    participant
}: {
    participantId: number
    moduleId: number
    participant: IUserParticipantModel
}): ReactElement {
    const { data, isFetching, isFetched }: TSuspenseQuery = useSuspenseQuery({
        participantId,
        moduleId
    })

    const builtData: ReturnType<typeof uiUtils.buildModuleListData> = useMemo(
        (): ReturnType<typeof uiUtils.buildModuleListData> => uiUtils.buildModuleListData(data),
        [data]
    )

    return (
        <Fragment>
            {isFetching && <Spinner size={ESpinnerSize.Large} variant={ESpinnerVariant.Accent} />}

            {isFetched && (
                <motion.div layout="position">
                    {map(
                        builtData,
                        (group, groupIndex: number): ReactElement => (
                            <motion.div
                                key={`${JSON.stringify(group.key.moduleTitle)}-${group.key.phaseProgressId}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "0px 0px -60px 0px" }}
                                transition={{ duration: 0.45, ease: "easeOut", delay: groupIndex * 0.5 }}
                            >
                                <elements.modulesTab.Module group={group} user={participant} />
                            </motion.div>
                        )
                    )}
                </motion.div>
            )}
        </Fragment>
    )
}
